import {
  GeneralInputOption,
  GeneralInputProps,
} from '@/components/GeneralInputs/type';
import { SuspenseSelect } from '@/components/Suspense';
import {
  useGetTransactionItemGeneralInputOption,
  useGetTransactionItemGeneralInputOptionsSuspenseInfinite,
} from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { useModelsToOptionsWithData } from '@/hooks/useTypeToOptions';
import { getPageData } from '@/utils/getPageData';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
  Skeleton,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export type PulldownTypeSelectProps<TFieldValues extends FieldValues> = Omit<
  FormSelectProps<TFieldValues>,
  'selectProps'
> & {
  selectProps?: SelectProps;
  pulldownId: string;
  optionId?: string;
  showDeletedOptionAsEmpty?: boolean;
};

type PulldownTypeSelectInputProps<TFieldValues extends FieldValues> =
  PulldownTypeSelectProps<TFieldValues> & GeneralInputProps;

const InnerPulldownTypeSelect = <TFieldValues extends FieldValues>({
  control,
  inputId,
  rules,
  name,
  placeholder,
  selectProps: _selectProps,
  pulldownId,
  optionId,
  showDeletedOptionAsEmpty = true,
}: PulldownTypeSelectInputProps<TFieldValues>) => {
  const containerRef = useGlobalContainerRef();
  const [query, setQuery] = useState<string>('');
  const { setValue } = useFormContext<TFieldValues>();

  const {
    data: apiData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
  } = useGetTransactionItemGeneralInputOptionsSuspenseInfinite(pulldownId, {
    keyword: query,
    per: 500,
    is_visible: true,
  });
  if (error) throw error;

  const data = useMemo(
    () => getPageData(apiData, 'ap_general_input_options'),
    [apiData]
  );

  const foundOption = useMemo(() => {
    return data?.find((option: GeneralInputOption) => option.id === optionId);
  }, [data, optionId]);

  const { data: optionApiData, isLoading: optionApiDataLoading } =
    useGetTransactionItemGeneralInputOption(
      pulldownId,
      optionId ?? '',
      optionId !== undefined && !foundOption && query === ''
    );

  const defaultOption = useMemo(() => {
    if (optionId === undefined) {
      return [];
    }
    const generateOption = (option: GeneralInputOption) => {
      return [
        {
          value: option.id ?? '',
          label: (option.label ?? '').concat(
            option.label ? `(${option.code})` : ''
          ),
          caption: option.code ?? '',
        },
      ];
    };
    if (foundOption) {
      return generateOption(foundOption);
    }

    if (optionApiData) {
      return generateOption(optionApiData);
    }

    return [];
  }, [optionId, foundOption, optionApiData]);

  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );

  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);

  const memoizedDefaultOption = useMemo(() => {
    if (defaultOption.length > 0) return defaultOption;
    // Option was deleted - set empty value to form value
    if (
      showDeletedOptionAsEmpty &&
      optionId &&
      !foundOption &&
      !optionApiDataLoading &&
      optionApiData === null
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setValue(name, '' as PathValue<TFieldValues, Path<TFieldValues>>);
    }
    return [];
  }, [
    defaultOption,
    showDeletedOptionAsEmpty,
    optionId,
    foundOption,
    optionApiDataLoading,
    optionApiData,
    setValue,
    name,
  ]);

  const options = useModelsToOptionsWithData<GeneralInputOption>(
    data ?? [],
    query,
    memoizedDefaultOption,
    {
      labelKeys: ['label'],
      caption: 'code',
    },
    true,
    true
  );

  const selectProps: SelectProps = useMemo(
    () => ({
      ..._selectProps,
      selectAll: undefined,
      selectAllLabel: undefined,
      options: options ?? [],
      mode: 'single',
      onSearchDebounceWait: 600,
      getPopupContainer: containerRef,
      comboBox: true,
      placeholder: placeholder,
      loading: isLoading,
      fetchMore: fetchMore,
      onSearch: onSearch,
    }),
    [
      containerRef,
      placeholder,
      _selectProps,
      isLoading,
      fetchMore,
      onSearch,
      options,
    ]
  );

  return (
    <>
      {optionApiDataLoading ? (
        <div className={cx(styles['skeleton-container'])}>
          <Skeleton.Input active size='default' block />
        </div>
      ) : (
        <FormSelect<TFieldValues>
          name={name}
          control={control}
          inputId={inputId}
          selectProps={selectProps}
          rules={rules}
        />
      )}
    </>
  );
};
InnerPulldownTypeSelect.displayName = 'InnerPulldownTypeSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const PulldownTypeSelect = memo((props) => (
  <SuspenseSelect>
    <InnerPulldownTypeSelect {...props} />
  </SuspenseSelect>
)) as typeof InnerPulldownTypeSelect;
PulldownTypeSelect.displayName = 'PulldownTypeSelect';
